// Unified API client - single source of truth for all API calls
const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8080').replace(/\/$/, '');

// No need for endpoints object - use strings directly

// Simple token management
const getAccessToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

const isTokenExpired = (): boolean => {
  const expiredAt = localStorage.getItem('expiredAt');
  if (!expiredAt) return true;
  return Date.now() >= parseInt(expiredAt);
};

const clearTokensAndRedirect = (): void => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('expiredAt');
  localStorage.removeItem('user');
  window.location.href = '/login';
};

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return null;

  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) throw new Error('Refresh failed');

    const data = await response.json();
    localStorage.setItem('accessToken', data.accessToken);
    localStorage.setItem('expiredAt', data.expiredAt);

    return data.accessToken;
  } catch (error) {
    // Clear all tokens on refresh failure
    clearTokensAndRedirect();
    return null;
  }
};

// Main API request function
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit & { skipAuth?: boolean } = {}
): Promise<T> {
  const { skipAuth = false, ...fetchOptions } = options;

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;

  // Don't set Content-Type for FormData - let browser set it automatically
  const isFormData = fetchOptions.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(fetchOptions.headers as Record<string, string>),
  };

  // Add auth token if not skipped and token exists
  if (!skipAuth) {
    let token = getAccessToken();

    // Try to refresh token if expired
    if (token && isTokenExpired()) {
      token = await refreshAccessToken();
    }

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      headers,
    });

    // Handle 401 Unauthorized
    if (response.status === 401 && !skipAuth) {
      clearTokensAndRedirect();
      throw new Error('Unauthorized - redirecting to login');
    }

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

      try {
        const errorJson = JSON.parse(errorText);
        if (typeof errorJson.message === 'object') {
          errorMessage = JSON.stringify(errorJson.message);
        } else {
          errorMessage = errorJson.message || errorMessage;
        }
        console.error('API Error Details:', errorJson);
        console.error('Parsed error message:', errorMessage);
        console.error('Full error response:', errorText);
      } catch {
        console.error('API Error (non-JSON):', errorText);
        // Use default error message if response is not JSON
      }

      throw new Error(errorMessage);
    }

    // Handle different response types
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      return await response.json();
    } else if (contentType?.includes('text/')) {
      return await response.text() as T;
    } else {
      return await response.blob() as T;
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error(`API request failed: ${endpoint}`, errorMessage);
    throw error;
  }
}

// Convenience methods
export const api = {
  get: <T>(endpoint: string, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  put: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  patch: <T>(endpoint: string, data?: any, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data instanceof FormData ? data : (data ? JSON.stringify(data) : undefined),
    }),

  delete: <T>(endpoint: string, options?: RequestInit & { skipAuth?: boolean }) =>
    apiRequest<T>(endpoint, { ...options, method: 'DELETE' }),
};

export default api;
